/**
 * The assistant console's pure logic: status of conversations and steps, the capability rules
 * table, the settings form, message checks, and the rows it adds to Needs you. Pure, so it is
 * tested (__tests__/model.test.ts).
 */
import {
  AI_CAPABILITIES,
  ASSISTANT_MESSAGE_MAX,
  CAPABILITY_TIERS,
  HARNESS_TOOLS,
  POOL_LAUNCH_ACCOUNT_ID,
  POOL_LAUNCH_ACCOUNT_LABEL,
  TIER_DESCRIPTION,
  TIER_LABEL,
  assistantSettingsError,
  decideAssistantCall,
  decideHarnessTool,
  harnessRuleKey,
  launchAccountAgent,
  launchAccountKindLabel,
  tierRank,
} from '@orgus/protocol';
import type {
  AiRole,
  AssistantSettings,
  AssistantSettingsPatch,
  AssistantState,
  AssistantStep,
  AutonomyMode,
  CapabilityRule,
  CapabilityTier,
  ConversationState,
  ConversationSummary,
  LaunchAccountsState,
  StepOutcome,
} from '@orgus/protocol';
import { formatInt } from '@orgus/ui';
import type { RowStatusIcon, RowTone } from '@orgus/ui';
import type { NeedItem } from '../overview/needsYou.ts';

export interface StatusLook {
  tone: RowTone;
  icon: RowStatusIcon;
  label: string;
}

/** A conversation's one status: icon plus label. Only working, needs approval and failed carry colour. */
export const CONVERSATION_STATUS: Record<ConversationState, StatusLook> = {
  idle: { tone: 'neutral', icon: 'idle', label: 'Ready' },
  running: { tone: 'working', icon: 'working', label: 'Working' },
  waiting: { tone: 'attention', icon: 'needs-you', label: 'Needs you' },
  failed: { tone: 'danger', icon: 'failed', label: 'Failed' },
};

/** A step's outcome: icon plus label. Done is grey: finished work is the default, not news. */
export const STEP_STATUS: Record<StepOutcome, StatusLook> = {
  running: { tone: 'working', icon: 'working', label: 'Running' },
  pending: { tone: 'attention', icon: 'needs-you', label: 'Waiting for approval' },
  ok: { tone: 'neutral', icon: 'done', label: 'Done' },
  error: { tone: 'danger', icon: 'failed', label: 'Failed' },
  denied: { tone: 'neutral', icon: 'stopped', label: 'Not allowed' },
  expired: { tone: 'neutral', icon: 'ended', label: 'Not approved in time' },
};

/** A reply is in flight: thinking, acting, or waiting on a person. */
export function isBusy(state: ConversationState): boolean {
  return state === 'running' || state === 'waiting';
}

/** The prompts the empty state offers, one click each. */
export const EXAMPLE_PROMPTS: readonly string[] = [
  'What needs me right now?',
  'Stop every idle session older than a day',
  "Summarise today's spend by project",
  'Log a bug for the last failed landing',
];

// ---------------------------------------------------------------------------
// Steps
// ---------------------------------------------------------------------------

const SUMMARY_MAX = 120;

function valueText(v: unknown): string {
  if (v == null) return 'none';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return `${formatInt(v.length)} ${v.length === 1 ? 'item' : 'items'}`;
  return '{…}';
}

/** The input on one line: "sessionId 7f3a, reason Idle for 2 days", clipped with an ellipsis. */
export function stepInputSummary(input: Readonly<Record<string, unknown>>): string {
  const parts = Object.entries(input).map(([k, v]) => `${k} ${valueText(v)}`.replace(/\s+/g, ' ').trim());
  const line = parts.join(', ');
  return line.length > SUMMARY_MAX ? `${line.slice(0, SUMMARY_MAX - 1).trimEnd()}…` : line;
}

/** The input as indented JSON, for the expanded step. */
export function stepInputJson(input: Readonly<Record<string, unknown>>): string {
  try {
    return JSON.stringify(input, null, 2);
  } catch {
    return '{}';
  }
}

/** What a pending step asks for, as a sentence: "The assistant wants to stop a session." */
export function approvalQuestion(step: Pick<AssistantStep, 'title'>): string {
  const t = step.title.trim();
  if (!t) return 'The assistant wants to make a change.';
  return `The assistant wants to ${t.charAt(0).toLowerCase()}${t.slice(1)}.`;
}

// ---------------------------------------------------------------------------
// Capability rules
// ---------------------------------------------------------------------------

/** 'default' follows the autonomy mode; the others are the per-capability overrides. */
export type RuleChoice = 'default' | CapabilityRule;
export const RULE_CHOICES: readonly RuleChoice[] = ['default', 'auto', 'ask', 'deny'];
export const RULE_CHOICE_LABEL: Record<RuleChoice, string> = { default: 'Default', auto: 'Run', ask: 'Ask first', deny: 'Never' };

/** The effect of a rule in words. */
export const VERDICT_LABEL: Record<'auto' | 'ask' | 'deny', string> = { auto: 'Runs', ask: 'Asks first', deny: 'Never runs' };

export interface RuleRow {
  /** The rule key: the capability name, or 'tool:<Name>' for a Claude Code tool. */
  key: string;
  /** What to show beside the title: the capability name, or the Claude Code tool name. */
  name: string;
  title: string;
  description: string;
  tier: CapabilityTier;
  choice: RuleChoice;
  /** What happens with this rule and mode, for someone at `role`. */
  verdict: 'auto' | 'ask' | 'deny';
  why: string;
  /** Run and Ask first change nothing for reads: they always run unless set to Never. */
  readOnly: boolean;
  /** A Claude Code tool rather than an Orgus capability. */
  tool: boolean;
}

export type RuleGroupId = CapabilityTier | 'harness';

export interface RuleGroup {
  id: RuleGroupId;
  label: string;
  description: string;
  rows: RuleRow[];
  /** Rows set to something other than Default. */
  overrides: number;
}

/** Steps and rules for Claude Code's own tools carry this prefix. */
export const TOOL_PREFIX = 'tool:';
export const isToolCapability = (capability: string): boolean => capability.startsWith(TOOL_PREFIX);

const HARNESS_DESCRIPTION = "Claude Code's own tools, when they are on. Each use is asked of Orgus first and decided like a capability.";

/** Claude Code's tools as rule rows, lowest tier first, in catalogue order within a tier. */
export function harnessRuleRows(rules: Readonly<Record<string, CapabilityRule>>, mode: AutonomyMode, role: AiRole = 'owner'): RuleRow[] {
  return Object.entries(HARNESS_TOOLS)
    .map(([name, t], i) => ({ name, t, i }))
    .sort((a, b) => tierRank(a.t.tier) - tierRank(b.t.tier) || a.i - b.i)
    .map(({ name, t }): RuleRow => {
      const key = harnessRuleKey(name);
      const d = decideHarnessTool(name, mode, rules, role);
      return { key, name, title: t.title, description: `Claude Code's ${name} tool. ${TIER_DESCRIPTION[t.tier]}`, tier: t.tier, choice: rules[key] ?? 'default', verdict: d.verdict, why: d.why, readOnly: t.tier === 'read', tool: true };
    });
}

/**
 * Every capability, grouped by tier, with its rule and what it does under `mode`; with
 * `harness` on, Claude Code's tools follow as their own group.
 */
export function ruleGroups(rules: Readonly<Record<string, CapabilityRule>>, mode: AutonomyMode, role: AiRole = 'owner', opts: { harness?: boolean } = {}): RuleGroup[] {
  const groups = CAPABILITY_TIERS.map((tier): RuleGroup => {
    const rows = AI_CAPABILITIES.filter((c) => c.tier === tier).map((c): RuleRow => {
      const d = decideAssistantCall(c.name, c.tier, mode, rules, role);
      return { key: c.name, name: c.name, title: c.title, description: c.description, tier, choice: rules[c.name] ?? 'default', verdict: d.verdict, why: d.why, readOnly: tier === 'read', tool: false };
    });
    return { id: tier, label: TIER_LABEL[tier], description: TIER_DESCRIPTION[tier], rows, overrides: rows.filter((r) => r.choice !== 'default').length };
  });
  if (opts.harness) {
    const rows = harnessRuleRows(rules, mode, role);
    groups.push({ id: 'harness', label: 'Claude Code tools', description: HARNESS_DESCRIPTION, rows, overrides: rows.filter((r) => r.choice !== 'default').length });
  }
  return groups;
}

/** The rules with one capability set; Default removes the override. */
export function withRule(rules: Readonly<Record<string, CapabilityRule>>, name: string, choice: RuleChoice): Record<string, CapabilityRule> {
  const next = { ...rules };
  if (choice === 'default') delete next[name];
  else next[name] = choice;
  return next;
}

export function sameRules(a: Readonly<Record<string, CapabilityRule>>, b: Readonly<Record<string, CapabilityRule>>): boolean {
  const ka = Object.keys(a);
  return ka.length === Object.keys(b).length && ka.every((k) => a[k] === b[k]);
}

// ---------------------------------------------------------------------------
// Settings form
// ---------------------------------------------------------------------------

/** The settings form: numbers as the person typed them. */
export interface SettingsForm {
  mode: AutonomyMode;
  model: string;
  /** '' lets Orgus pick. */
  machineId: string;
  /** '' runs on the workspace default (Settings, Agent accounts). */
  launchAccountId: string;
  budget: string;
  /** Minutes. */
  timeLimit: string;
  /** Minutes. */
  approvalWait: string;
}

const minutes = (ms: number): string => {
  const m = ms / 60_000;
  return Number.isInteger(m) ? String(m) : String(Math.round(m * 10) / 10);
};

export function settingsToForm(s: AssistantSettings): SettingsForm {
  return { mode: s.mode, model: s.model, machineId: s.machineId ?? '', launchAccountId: s.launchAccountId ?? '', budget: String(s.maxBudgetUsd), timeLimit: minutes(s.timeoutMs), approvalWait: minutes(s.approvalTimeoutMs) };
}

export type SettingsErrors = Partial<Record<'budget' | 'timeLimit' | 'approvalWait' | 'model' | 'form', string>>;

const num = (s: string): number => (s.trim() === '' ? Number.NaN : Number(s.trim()));

/** The patch the form asks for (only what changed), or the errors to show under the fields. */
export function formToPatch(f: SettingsForm, current: AssistantSettings): { patch: AssistantSettingsPatch; errors: SettingsErrors } {
  const errors: SettingsErrors = {};
  const budget = num(f.budget);
  const timeLimit = num(f.timeLimit);
  const approvalWait = num(f.approvalWait);
  if (!(budget > 0 && budget <= 100)) errors.budget = 'Enter an amount above $0 and up to $100.';
  if (!(timeLimit >= 1 && timeLimit <= 240)) errors.timeLimit = 'Enter 1 to 240 minutes.';
  if (!(approvalWait >= 0.5 && approvalWait <= 120)) errors.approvalWait = 'Enter 0.5 to 120 minutes.';
  // An empty model is valid: it runs the machine's own Claude Code default.
  if (Object.keys(errors).length) return { patch: {}, errors };

  const next: AssistantSettingsPatch = {
    mode: f.mode,
    model: f.model.trim(),
    machineId: f.machineId || null,
    launchAccountId: f.launchAccountId || null,
    maxBudgetUsd: budget,
    timeoutMs: Math.round(timeLimit * 60_000),
    approvalTimeoutMs: Math.round(approvalWait * 60_000),
  };
  const patch: AssistantSettingsPatch = {};
  for (const [k, v] of Object.entries(next) as [keyof AssistantSettingsPatch, unknown][]) {
    // A hub from before the account setting has none: unset there is the default.
    if ((current[k] ?? null) !== v) (patch as Record<string, unknown>)[k] = v;
  }
  const invalid = assistantSettingsError(patch);
  if (invalid) return { patch: {}, errors: { form: invalid } };
  return { patch, errors };
}

// ---------------------------------------------------------------------------
// Messages and selection
// ---------------------------------------------------------------------------

/** Why this message cannot be sent, or null. */
export function messageError(text: string): string | null {
  if (!text.trim()) return 'Write a message first.';
  if (text.length > ASSISTANT_MESSAGE_MAX) return `Keep the message to ${formatInt(ASSISTANT_MESSAGE_MAX)} characters. It has ${formatInt(text.length)}.`;
  return null;
}

/**
 * Which conversation the page shows. ?c=new (or no conversations) is a new one (null);
 * ?c=<id> is that one; no parameter opens the newest.
 */
export function pickConversation(param: string | null, conversations: readonly Pick<ConversationSummary, 'id'>[]): string | null {
  if (param === 'new') return null;
  if (param) return param;
  return conversations[0]?.id ?? null;
}

/** Conversations newest first by last activity. */
export function sortConversations<T extends Pick<ConversationSummary, 'updatedAt'>>(list: readonly T[]): T[] {
  return [...list].sort((a, b) => b.updatedAt - a.updatedAt);
}

// ---------------------------------------------------------------------------
// Needs you
// ---------------------------------------------------------------------------

/** Steps waiting for a person, as Needs you rows, oldest first, each one click from its conversation. */
export function assistantNeeds(pending: AssistantState['pending']): NeedItem[] {
  return [...pending]
    .sort((a, b) => a.at - b.at)
    .map((p) => ({
      key: `as:${p.step.approval?.id ?? p.entryId}`,
      kind: 'session',
      title: p.step.title,
      detail: `${TIER_LABEL[p.step.tier]} step. The assistant asks in "${p.conversationTitle}".`,
      where: 'Assistant',
      projectId: null,
      at: p.at,
      to: { path: `/assistant?c=${encodeURIComponent(p.conversationId)}` },
      verb: 'Review',
    }));
}

// ---------------------------------------------------------------------------
// The account replies run on (ADR 0107 amendment, 24 Oct 2026)
// ---------------------------------------------------------------------------

/** What the workspace default is for replies, in words. */
export function defaultAccountLine(state: Pick<LaunchAccountsState, 'pools'> | null): string {
  if (state?.pools?.['claude-code']?.whenUnset) return 'The workspace default is the Claude Code pool.';
  return "The workspace default is each machine's own sign-in; a machine without one uses the first Claude account.";
}

/** The accounts a reply may run on: the workspace default, each Claude Code account, the pool once it exists, and a chosen one that is gone. */
export function assistantAccountOptions(state: Pick<LaunchAccountsState, 'accounts' | 'pools'> | null, chosen: string): { value: string; label: string }[] {
  const accounts = (state?.accounts ?? []).filter((a) => launchAccountAgent(a) === 'claude-code');
  const options = [
    { value: '', label: 'Workspace default' },
    ...accounts.map((a) => ({ value: a.id, label: a.builtIn ? "Each machine's own sign-in" : `${a.name}, ${launchAccountKindLabel('claude-code', a.kind)}` })),
    ...(state?.pools?.['claude-code'] ? [{ value: POOL_LAUNCH_ACCOUNT_ID, label: POOL_LAUNCH_ACCOUNT_LABEL }] : []),
  ];
  if (chosen && !options.some((o) => o.value === chosen)) options.push({ value: chosen, label: 'An account that no longer exists' });
  return options;
}
